import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import Flatpickr from 'react-flatpickr';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import CountrySelect from 'Components/Common/CountrySelect';
import DeletionRequestsTable from 'Components/collaboration/deletion-request/DeletionRequestsTable';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { fetchCampaigns } from 'services/api/collaboration';
import {
    defaultDeletionRequestFilters,
    updateDeletionRequestFilters,
} from 'slices/collaboration-deletion-requests/reducer';
import { fetchCollaborationDeletionRequestsThunk, DELETION_REQUEST_FILTER_KEY_EXCLUDES } from 'slices/collaboration-deletion-requests/thunk';
import { AppDispatch, RootState } from 'store';
import { OptionType } from 'types';
import {
    CollabDeletionRequestStatus,
    CollabDeletionRequestsFilters,
} from 'types/collaboration-deletion-request';
import { decodeBase64, encodeBase64, generateUniqueKey } from 'utils/helpers';
import './style.scss';

const STATUS_OPTIONS: { value: CollabDeletionRequestStatus | ''; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'complete', label: 'Complete' },
    { value: 'rejected', label: 'Rejected' },
];

const hasActiveFilters = (filters: CollabDeletionRequestsFilters) =>
    Boolean(filters.q?.trim()) ||
    Boolean(filters.campaign_id) ||
    Boolean(filters.country) ||
    Boolean(filters.start_date) ||
    Boolean(filters.end_date) ||
    Boolean(filters.status);

const CollabDeletionRequests = () => {
    useDocumentTitle('Collab Deletion Requests');

    const dispatch: AppDispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const queryFilters = searchParams.get('filters');

    useEffect(() => {
        const decodedFilters = queryFilters
            ? decodeBase64(queryFilters, defaultDeletionRequestFilters)
            : defaultDeletionRequestFilters;
        dispatch(
            updateDeletionRequestFilters({
                ...defaultDeletionRequestFilters,
                ...decodedFilters,
                page: 1,
            })
        );
    }, [queryFilters, dispatch]);

    const filters = useSelector((state: RootState) => state.collaborationDeletionRequests.filters);
    const filterId = useMemo(
        () => generateUniqueKey(filters, DELETION_REQUEST_FILTER_KEY_EXCLUDES),
        [filters]
    );

    const requestsData = useSelector(
        (state: RootState) => state.collaborationDeletionRequests.filtered_lists[filterId]
    );
    const requests = requestsData?.list ?? [];
    const page = requestsData?.page ?? 1;
    const hasMore = requestsData?.has_more ?? false;
    const count = requestsData?.count ?? 0;

    const loading = useSelector((state: RootState) => state.collaborationDeletionRequests.loading);
    const error = useSelector((state: RootState) => state.collaborationDeletionRequests.error);

    const [initialLoading, setInitialLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const setFilters = useCallback(
        (updatedFilters: CollabDeletionRequestsFilters) => {
            const { page: _page, ...rest } = updatedFilters;
            const cleanedFilters = Object.fromEntries(
                Object.entries(rest).filter(([, value]) => {
                    if (value === undefined || value === null || value === '') return false;
                    return true;
                })
            );

            if (Object.keys(cleanedFilters).length > 0) {
                setSearchParams({ filters: encodeBase64(cleanedFilters) });
            } else {
                setSearchParams({});
            }
        },
        [setSearchParams]
    );

    useEffect(() => {
        setSearchQuery(filters.q ?? '');
    }, [filters.q]);

    useEffect(() => {
        if (filters.page === 1) {
            setInitialLoading(true);
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchCollaborationDeletionRequestsThunk({ filters })).finally(() => {
                setInitialLoading(false);
            });
        }, 500);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [filters, dispatch]);

    const searchTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, []);

    const searchRequests = (q: string) => {
        setSearchQuery(q);

        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            const trimmed = q.trim();
            if (trimmed.length > 2 || trimmed.length === 0) {
                setFilters({ ...filtersRef.current, q: trimmed, page: 1 });
            }
        }, 500);
    };

    const nextPage = () => {
        if (hasMore && !loading && requests.length > 0) {
            dispatch(
                fetchCollaborationDeletionRequestsThunk({
                    filters: { ...filters, page: page + 1 },
                })
            );
        }
    };

    const loadCampaigns = useMemo(
        () =>
            debounce((query: string, callback: (options: OptionType[]) => void) => {
                if (!query || query.length < 3) {
                    callback([]);
                    return;
                }

                fetchCampaigns(query)
                    .then((resp) => {
                        const items = Array.isArray(resp) ? resp : [];
                        callback(
                            items.map((item: { id: number | string; name: string }) => ({
                                value: String(item.id),
                                label: item.name,
                            }))
                        );
                    })
                    .catch(() => callback([]));
            }, 200),
        []
    );

    const dateRangeValue = useMemo(() => {
        if (filters.start_date && filters.end_date) {
            return [filters.start_date, filters.end_date];
        }
        return [];
    }, [filters.start_date, filters.end_date]);

    const recordsLabel =
        !initialLoading && requests.length > 0
            ? `Showing ${requests.length} of ${count} records`
            : null;

    return (
        <div className="page-content deletion-requests-page">
            <div className="deletion-requests-header">
                <h1 className="deletion-requests-title">Collab Deletion Requests</h1>
            </div>

            <div className="deletion-requests-toolbar">
                <div className="deletion-requests-search">
                    <i className="ri-search-2-line" />
                    <input
                        type="text"
                        placeholder="Search creator..."
                        value={searchQuery}
                        onChange={(e) => searchRequests(e.target.value)}
                    />
                </div>

                <div className="deletion-requests-campaign">
                    <AsyncSelect
                        value={
                            typeof filters.campaign === 'string'
                                ? null
                                : filters.campaign ?? null
                        }
                        onChange={(val) => {
                            setFilters({
                                ...filters,
                                campaign: val ?? null,
                                campaign_id: val?.value,
                                page: 1,
                            });
                        }}
                        placeholder="Campaign"
                        cacheOptions
                        isClearable
                        loadOptions={loadCampaigns}
                        classNamePrefix="deletion-requests-select"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 20 }) }}
                        noOptionsMessage={({ inputValue }) =>
                            inputValue.length < 3 ? 'Type 3+ characters' : 'No campaigns found'
                        }
                    />
                </div>

                <div className="deletion-requests-country">
                    <CountrySelect
                        value={filters.country || null}
                        onChange={(country) =>
                            setFilters({
                                ...filters,
                                country: country ?? '',
                                page: 1,
                            })
                        }
                        placeholder="Country"
                    />
                </div>

                <div className="deletion-requests-dates">
                    <Flatpickr
                        className="deletion-requests-datepicker"
                        placeholder="Created date"
                        value={dateRangeValue}
                        options={{
                            mode: 'range',
                            dateFormat: 'Y-m-d',
                            altInput: true,
                            altFormat: 'd M, Y',
                        }}
                        onChange={(selectedDates) => {
                            if (selectedDates.length === 0) {
                                setFilters({
                                    ...filters,
                                    start_date: '',
                                    end_date: '',
                                    page: 1,
                                });
                                return;
                            }
                            if (selectedDates.length < 2) return;

                            const start = selectedDates[0];
                            const end = selectedDates[1];
                            const pad = (n: number) => String(n).padStart(2, '0');
                            const toYmd = (date: Date) =>
                                `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

                            setFilters({
                                ...filters,
                                start_date: toYmd(start),
                                end_date: toYmd(end),
                                page: 1,
                            });
                        }}
                    />
                    <i className="ri-calendar-2-line" />
                </div>

                <div className="deletion-requests-status">
                    {STATUS_OPTIONS.map((option) => (
                        <button
                            key={option.value || 'all'}
                            type="button"
                            className={`deletion-requests-chip ${
                                (filters.status || '') === option.value ? 'is-active' : ''
                            }`}
                            onClick={() =>
                                setFilters({
                                    ...filters,
                                    status: option.value,
                                    page: 1,
                                })
                            }
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {hasActiveFilters(filters) && (
                    <button
                        type="button"
                        className="deletion-requests-reset"
                        onClick={() => setFilters({ ...defaultDeletionRequestFilters })}
                    >
                        Reset
                    </button>
                )}
            </div>

            {recordsLabel && <p className="deletion-requests-count">{recordsLabel}</p>}

            <DeletionRequestsTable
                requests={requests}
                hasMore={hasMore}
                loading={loading}
                initialLoading={initialLoading}
                error={error}
                onNextPage={nextPage}
            />

            {recordsLabel && <p className="deletion-requests-count deletion-requests-count--bottom">{recordsLabel}</p>}
        </div>
    );
};

export default CollabDeletionRequests;
